import { AxiosHeaders, type RawAxiosRequestHeaders } from 'axios';
import HttpCodes from 'http-status-codes';
import _ from 'lodash';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { getAxiosInstance } from '@/requests/axios.utilities';
import { generateRequestId } from '@/utilities/http.utilities';
import { decrementPendingRequests, getRequestIdFromHeaders, isPendingRequest } from '@seeqdev/api';
import type { SeeqInternalAxiosRequestConfig } from '@/requests/axios.utilities';

type RetryRequestHeaders = SeeqInternalAxiosRequestConfig['headers'] | RawAxiosRequestHeaders | undefined;
type AxiosHeadersInit = Parameters<typeof AxiosHeaders.from>[0];

type RetryRequestConfig = Partial<Omit<SeeqInternalAxiosRequestConfig, 'headers'>> & {
  retry?: number;
  headers?: RetryRequestHeaders;
};

type RetryableSeeqRequestConfig = SeeqInternalAxiosRequestConfig & {
  retry?: number;
};

type ConflictAxiosError = {
  response?: {
    status?: number;
    config?: RetryRequestConfig;
  };
};

export function conflictRetryInterceptor(error?: ConflictAxiosError): Promise<unknown> {
  const MAXIMUM_RETRIES = 5;
  if (!error) {
    return Promise.reject(error);
  }

  const responseStatus = _.get(error, 'response.status');
  if (responseStatus !== HttpCodes.CONFLICT || !error.response?.config) {
    return Promise.reject(error);
  }

  const { config } = error.response;
  config.retry = config.retry || 0;

  if (config.retry < MAXIMUM_RETRIES) {
    // Before retrying, drop the pending count only if the request ID is still present in the `@seeqdev/api`'s pending
    // requests list. This interceptor returns a new request instead of throwing, so later error interceptors won't run. If
    // pending interceptors were attached (and/or the request wasn't silenced), they will already have decremented by the time
    // we get here so don't attempt to decrement again if the request ID is no longer pending.
    const requestId = getRequestIdFromHeaders(config.headers as AxiosHeaders);
    if (isPendingRequest(requestId)) {
      decrementPendingRequests(requestId);
    }

    config.retry += 1;
    const normalizedHeaders = normalizeHeaders(config.headers);
    normalizedHeaders.set(SeeqNames.API.Headers.RequestId, generateRequestId());

    const retryConfig: RetryableSeeqRequestConfig = { ...config, headers: normalizedHeaders };
    return Promise.resolve().then(() => getAxiosInstance().request(retryConfig));
  }

  return Promise.reject(error);
}

function normalizeHeaders(headers?: RetryRequestHeaders) {
  if (!headers) {
    return new AxiosHeaders();
  }
  if (headers instanceof AxiosHeaders) {
    return AxiosHeaders.from(headers);
  }
  return AxiosHeaders.from(headers as AxiosHeadersInit);
}
